<!-- Search, sort, count and export -->
<div class="gl-toolbar">
  <app-search-input
    class="gl-search"
    placeholder="Search name, phone or email"
    [debounce]="350"
    (valueChange)="updateSearchTerm($event)"
  />

  <app-sort-dropdown
    [groups]="sortGroups"
    [selectedValue]="sortCriteria"
    (selectionChange)="updateSortCriteria($any($event))"
  />

  <span class="gl-toolbar__spacer"></span>

  <p class="gl-count" aria-live="polite">{{ countLine }}</p>

  <button
    *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'export-contacts' }"
    type="button"
    class="bk-btn bk-btn--ghost"
    [disabled]="exporting || !pagination.totalElements"
    (click)="exportMembers()"
  >
    <span class="bk-sym" aria-hidden="true">download</span>
    {{ exportLabel }}
  </button>
</div>

<!-- Export progress and notices. The status line speaks at the start and the end only. -->
<p class="bk-sr-only" role="status">{{ exportStatus }}</p>
@if (exporting) {
  <div class="gl-notice">
    <span class="gl-notice__text" aria-hidden="true">
      Exporting {{ formatCount(exportDone) }} of {{ formatCount(exportTarget) }} members
    </span>
    <span class="gl-progress" aria-hidden="true">
      <span class="gl-progress__fill" [style.transform]="'scaleX(' + exportProgress + ')'"></span>
    </span>
    <button type="button" class="bk-more" (click)="cancelExport()">Cancel</button>
  </div>
} @else if (exportNotice || exportError) {
  <div class="gl-notice" [class.is-error]="!!exportError" [attr.role]="exportError ? 'alert' : null">
    <span class="gl-notice__text">{{ exportError || exportNotice }}</span>
    <button type="button" class="bk-icon-btn" aria-label="Dismiss" (click)="dismissExportNotice()">
      <span class="bk-sym" aria-hidden="true">close</span>
    </button>
  </div>
}

<!-- Selection bar: what to do with the ticked members -->
@if (selection.size) {
  <div class="gl-select" role="region" aria-label="Selected members">
    <span class="gl-select__count">{{ selectionLabel }}</span>
    <span class="gl-select__actions">
      <button
        *hasPermission="{ category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'compose-sms' }"
        type="button"
        class="gl-select__btn"
        (click)="runAction('send-sms')"
      >
        <span class="bk-sym" aria-hidden="true">send</span>
        Message
      </button>
      @if (canRemove) {
        <button
          *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'manage-contact-groups' }"
          type="button"
          class="gl-select__btn"
          (click)="runAction('remove')"
        >
          <span class="bk-sym" aria-hidden="true">group_remove</span>
          Remove from {{ groupNoun }}
        </button>
      } @else {
        <ng-container *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'manage-contact-groups' }">
          <button type="button" class="gl-select__btn" (click)="runAction('move-to-organization')">
            <span class="bk-sym" aria-hidden="true">domain_add</span>
            Add to organization
          </button>
          <button type="button" class="gl-select__btn" (click)="runAction('add-tags')">
            <span class="bk-sym" aria-hidden="true">sell</span>
            Add tags
          </button>
        </ng-container>
        <button
          *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'delete-contacts' }"
          type="button"
          class="gl-select__btn"
          (click)="runAction('delete')"
        >
          <span class="bk-sym" aria-hidden="true">delete</span>
          Delete contacts
        </button>
      }
    </span>
    <button type="button" class="gl-select__clear" (click)="unselectAll()">Clear selection</button>
  </div>
}

<div class="gt gl-table">
  <div class="bk-panel gt__panel">
    <data-table label="Contacts in this group" [seamless]="true" [loading]="loading" [maxHeight]="'640px'" [skeletonColumns]="6">
      @if (loadError) {
        <div class="gt-error" role="alert">
          <p class="gt-error__text">{{ loadError }}</p>
          <button type="button" class="bk-btn bk-btn--ghost" (click)="load()">Load again</button>
        </div>
      } @else if (rows.length > 0) {
        <table class="bk-log">
          <thead>
            <tr>
              <th scope="col" class="col-check">
                <label class="gt-check-hit">
                  <input
                    type="checkbox"
                    class="gt-check"
                    [checked]="allContactsSelected"
                    [indeterminate]="someOnPageSelected"
                    (change)="toggleCheckAll()"
                    aria-label="Select every member on this page"
                  />
                  <span class="gt-check-all" aria-hidden="true">Select all</span>
                </label>
              </th>
              <th scope="col" class="col-name">Contact</th>
              <th scope="col" class="col-phone">Phone</th>
              <th scope="col" class="col-a">Country</th>
              <th scope="col" class="col-b">Gender</th>
              <th scope="col" class="col-c">Added</th>
              <th scope="col" class="col-actions"><span class="bk-sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows; track row.key) {
              <tr
                class="is-link"
                tabindex="0"
                [class.is-selected]="checkedMember(row.contact)"
                (click)="openRow(row)"
                (keydown.enter)="openFromKeyboard($event, row)"
              >
                <td class="col-check" (click)="$event.stopPropagation()">
                  <label class="gt-check-hit">
                    <input
                      type="checkbox"
                      class="gt-check"
                      [checked]="checkedMember(row.contact)"
                      (change)="select(row.contact)"
                      [attr.aria-label]="'Select ' + row.name"
                    />
                  </label>
                </td>
                <td class="col-name">
                  <span class="bk-msg">
                    <span class="bk-msg__subject">{{ row.name }}</span>
                    <span class="bk-msg__preview">
                      @if (row.email) {
                        {{ row.email }}
                      } @else {
                        <span class="gt-faint">No email</span>
                      }
                    </span>
                  </span>
                </td>
                <td class="col-phone">
                  <span class="gm-phone">
                    <span>{{ row.phone }}</span>
                    @if (row.phoneIssue) {
                      <app-status class="gm-phone__issue" [text]="row.phoneIssue" status="failed" />
                    }
                  </span>
                </td>
                <td class="quiet col-a">{{ row.country }}</td>
                <td class="quiet col-b">{{ row.gender }}</td>
                <td class="quiet col-c" [attr.title]="row.addedTitle">{{ row.added }}</td>
                <td class="col-actions">
                  <span class="bk-row-actions">
                    @if (canRemove) {
                      <button
                        *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'manage-contact-groups' }"
                        type="button"
                        class="bk-icon-btn"
                        [attr.aria-label]="'Remove ' + row.name + ' from this ' + groupNoun"
                        [matTooltip]="'Remove from ' + groupNoun"
                        (click)="$event.stopPropagation(); removeContact.emit(row.contact)"
                      >
                        <span class="bk-sym" aria-hidden="true">group_remove</span>
                      </button>
                    }
                    <button
                      *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'edit-contacts' }"
                      type="button"
                      class="bk-icon-btn"
                      [attr.aria-label]="'Edit ' + row.name"
                      matTooltip="Edit contact"
                      (click)="$event.stopPropagation(); openEditContact(row.contact)"
                    >
                      <span class="bk-sym" aria-hidden="true">edit</span>
                    </button>
                    <button
                      *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'delete-contacts' }"
                      type="button"
                      class="bk-icon-btn gt-danger"
                      [attr.aria-label]="'Delete ' + row.name + ' from your contacts'"
                      matTooltip="Delete contact"
                      (click)="$event.stopPropagation(); openDeletePrompt(row.contact)"
                    >
                      <span class="bk-sym" aria-hidden="true">delete</span>
                    </button>
                    <span class="bk-sym row-go" aria-hidden="true">chevron_right</span>
                  </span>
                </td>
              </tr>
            }
          </tbody>
        </table>
      } @else {
        <app-empty-state
          icon="contacts"
          [message]="emptyMessage"
          [hint]="searchTerm.trim() ? '' : emptyHint"
          [query]="searchTerm"
          size="medium"
          (clear)="clearSearch()"
        />
      }
    </data-table>
  </div>

  @if (!loadError && pagination.totalElements > 0) {
    <div class="gt__pager">
      <app-pagination
        (changePage)="updateContactsPage($event)"
        [currentPage]="pagination.pageNumber"
        [totalPages]="totalPages"
        [totalItems]="pagination.totalElements"
        [pageSize]="pageSize"
        (onNext)="goToNextPage()"
        (onPrev)="goToPreviousPage()"
        [loading]="loading"
        (pageSizeChange)="onPageSizeChange($event)"
      />
    </div>
  }
</div>

<!-- MODALS -->
@if (openEditContactModal) {
  <edit-contact-modal [selectedContact]="selectedContact" (closeModalEvent)="closeEditContact()" />
}

@if (showContactDetail) {
  <contact-detail (closeModal)="closeContactDetail()" [contact]="selectedContact" />
}

<app-prompt-card
  [title]="'Delete ' + selectedName + '?'"
  buttonText="Delete contact"
  tone="danger"
  impact="This cannot be undone."
  impactIcon="warning"
  [openModal]="showDeletePrompt"
  (onClose)="closeDeletePrompt()"
  (onButtonClick)="deleteContact()"
  [loading]="deleting"
>
  <p class="gt-prompt">
    <strong>{{ selectedName }}</strong> will be deleted from your contacts, not just this {{ groupNoun }}. They leave
    every organization, tag and segment.
  </p>
</app-prompt-card>
